import React, { useState, useEffect, useRef } from "react";
import { BASE_URL, DEDICATION_OPTIONS, REASON_PROVIDES_OPTIONS, REASON_WHO_OPTIONS } from "../common/constants";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

const EcardCustomizer = ({ setDonation, donation, isEditLoad, goToNextStep, addTransitions }) => {
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowRest(true);
    }, 2500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (isEditLoad) {
      setShowRest(true);
    }
  }, [isEditLoad]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (donation.dedication === "none") {
      donation.dedication = null;
    }

    const res = await fetch(BASE_URL + "/donations/" + donation.stripe_payment_intent_id, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ donation }),
    });
    const data = await res.json();
    // if data.errors...
    setDonation(data);
    goToNextStep();
  };

  const customizerForm = useRef(null);

  useGSAP(() => {
    const introAnimation = gsap.to(customizerForm.current, { opacity: 1, duration: 0.5 });
    addTransitions(introAnimation, "ecard-custom-intro", false); // intro animation

    const exitAnimation = gsap.to(customizerForm.current, { opacity: 0, duration: 0.5 });
    addTransitions(exitAnimation, "ecard-custom-exit", true); // exit animation
  }, [addTransitions]);

  return (
    <form ref={customizerForm} className="form start-hidden" onSubmit={submit}>
      <div className={"show-rest " + (showRest ? "ready" : "")}>
        {!isEditLoad && <>
          <h1 className="visually-hidden">Thank you for your support!</h1>
          <h2 className="heading">Finishing Touches</h2>
        </>}
        {isEditLoad && <>
          <h2 className="heading">Update Your Donation</h2>
          <fieldset className="dedication">
            <legend className="text-size-medium bold">How would you like to be recognized?</legend>
            <div>
              <label htmlFor="name">Full Name or Company Name</label>
              <input
                type="text"
                id="name"
                className="text-size-medium"
                name="recognition_name"
                value={donation.recognition_name || ""}
                required
                maxLength={255}
                onChange={(e) => setDonation({ ...donation, recognition_name: e.target.value })}
              />
            </div>
            <div className="checkbox">
              <label htmlFor="name_hidden">
                <input
                  type="checkbox"
                  id="name_hidden"
                  name="name_hidden"
                  className="visually-hidden"
                  checked={donation.name_hidden || false}
                  onChange={(e) => setDonation({ ...donation, name_hidden: e.target.checked })}
                />
                <span className="p">Don't publish this name</span>
              </label>
            </div>
          </fieldset>
        </>}

        <fieldset className="dedication">
          <legend className="text-size-medium bold">Would you like to dedicate your donation to someone?</legend>
          <div className="dedication-radio">
            {DEDICATION_OPTIONS.map((option) => (
              <label key={option.label} className="card card--radio">
                <input
                  type="radio"
                  name="dedication"
                  value={option.value}
                  onChange={(e) => setDonation({ ...donation, dedication: e.target.value })}
                  checked={donation.dedication ? (option.value === donation.dedication) : (option.value === "none")}
                />
                <span className="p">{option.label}</span>
              </label>
            ))}
          </div>

          {donation.dedication !== "none" && donation.dedication && (
            <div className="card card--text">
              <label htmlFor="dedication_name">Name</label>
              <input
                type="text"
                id="dedication_name"
                name="dedication_name"
                maxLength={255}
                value={donation.dedication_name || ""}
                onChange={(e) => setDonation({ ...donation, dedication_name: e.target.value })}
                required={donation.dedication !== "none"}
              />
            </div>
          )}
        </fieldset>

        <fieldset className="why-did-you-donate">
          <legend className="text-size-medium bold">Why did you donate{!isEditLoad && " today"}?</legend>
          <p className="text-size-medium">I bought a brick for First Forward because...</p>
          <p className="text-size-medium">
            FIRST UNITED provides
            <span className="select-wrapper">
              <select
                className="mark"
                name="reason_provides"
                id="reason_provides"
                value={donation.reason_provides}
                onChange={(e) => setDonation({ ...donation, reason_provides: e.target.value })}
              >
                <option value="">Choose one</option>
                {REASON_PROVIDES_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </span>
            <br />
            to people who
            <span className="select-wrapper">
              <select
                className="mark"
                name="reason_who"
                id="reason_who"
                value={donation.reason_who}
                onChange={(e) => setDonation({ ...donation, reason_who: e.target.value })}
              >
                <option value="">Choose one</option>
                {REASON_WHO_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </span>
          </p>
        </fieldset>

        {!isEditLoad && <>
          <h2 className="heading" aria-hidden="true">
            Thank you for your support!
          </h2>
          <p className="receipt-message">A receipt has been sent to {donation.email}</p>
        </>}
        {isEditLoad && <p className="text-size-medium">When you click Save and Finish, we will display an updated certificate that you can download or share. The new certificate will not be emailed to you.</p>}

        <button className="button" type="submit">
          Save and Finish
        </button>
      </div>
    </form>
  );
};

export { EcardCustomizer };
